import React, { useState } from 'react';
import styled from 'styled-components';

import { SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { SkyView } from '../../../viewer/skyview';
import { viewerZIndices } from '../../../viewer/viewer-z-indices';
import { useNonce } from '../../hooks/use-nonce';
import { usePreventPageUnload } from '../../hooks/use-prevent-page-unload';
import { useSubscribe } from '../../hooks/use-subscribe';
import { AppContext } from '../../state/app-state';
import { ChipStyled } from '../chip/chip';
import { PoweredBy } from '../powered-by/powered-by';
import { BottomMenu } from '../skyview/bottom-menu/bottom-menu';
import { AsbuiltPanelStyled } from '../skyview/panels/asbuilt/asbuilt-panel';
import { RightMenu, RightMenuStyled } from '../skyview/right-menu/right-menu';
import { SkyViewSpinner } from '../skyview/skyview-spinner';
import { SkyviewState } from '../skyview/skyview-state';
import { Stack } from '../stack/stack';
import { WmsAttributions } from '../wms-map/wms-attributions';
import { AssetMenu } from './asset-menu/asset-menu';
import {
  AssetMenuThumb,
  CollapsableContainer,
  CollapsableContainerStyled,
} from './asset-menu/collapsable-container';
import { ScreenCompass } from './asset-menu/components/label-renderer/labels/compass-labels/screen-compass';
import { CameraViews, CameraViewsStyled } from './camera-views';
import { LayerLoadingStatusStack } from './components/layer-loading-status/layer-loading-status-stack';
import { ShareViewStyled } from './components/share-view/share-view';
import { TimelineBar } from './components/timeline-bar/timeline-bar';
import { WmsSkyViewBridge } from './components/wms-skyview-bridge';
import { FlySpeedIndicator } from './fly-speed-indicator';
import { SkyviewChipsContainer } from './skyview-chips-container';
import { AssetFilterProvider } from './state/asset-filter-store';
import { AssetsProvider } from './state/assets-store';
import { Toolbar } from './toolbar/toolbar';
import { ViewerBottomInfoBar } from './viewer-bottom-info-bar';

type Props = {
  skyView: SkyView;
  assetMenuVisible?: boolean;
  children?: React.ReactNode;
  showSpinner?: boolean;
};

const collapedContainerWidth = 410;

const Skyview = (props: Props) => {
  usePreventPageUnload();

  const { isMobileApplication } = React.useContext(AppContext);
  const [assetMenuCollapsed, setAssetMenuCollapsed] = useState(false);
  const [, refresh] = useNonce();

  // Required as refresh is no automatically triggered when scene is changed.
  // Remove once Skyview is context based.
  useSubscribe(SubscriptionTopic.OnSceneChangeCompleted, refresh);

  return (
    <SkyviewState skyview={props.skyView}>
      <AssetsProvider>
        <AssetFilterProvider>
          {props.children}
          {
            // Placing outside of Component because it blocks all pointer events, disabling interaction with the WMS-map
            <WmsSkyViewBridge />
          }
          {isMobileApplication || (
            <Component assetMenuVisible={props.assetMenuVisible}>
              {props.assetMenuVisible !== false && (
                <CollapsableContainer
                  collapsed={assetMenuCollapsed}
                  data-asset-menu-root="true"
                  width={collapedContainerWidth}
                >
                  <AssetMenu />
                </CollapsableContainer>
              )}
              <Center>
                {props.showSpinner && <SkyViewSpinner />}
                <ViewerContents direction="row">
                  {props.assetMenuVisible !== false && (
                    <AssetMenuThumb
                      collapsed={assetMenuCollapsed}
                      setCollapsed={setAssetMenuCollapsed}
                    />
                  )}
                  <Center>
                    <WmsAttributions />
                    {props.skyView.sharedView || <SkyviewChipsContainer />}
                    <TimelineBar />
                    <LayerLoadingStatusStack />
                    <CameraViews />
                    <FlySpeedIndicator />
                    <PoweredBy dx={80} dy={10} position={'absolute-bottom-right'} />
                    <Toolbar />
                    <ScreenCompass />
                  </Center>
                </ViewerContents>
                <BottomMenu />
              </Center>
              <RightMenu />
              <ViewerBottomInfoBar />
            </Component>
          )}
        </AssetFilterProvider>
      </AssetsProvider>
    </SkyviewState>
  );
};

// Component position absolute in order to render above skyview.
// This is why pointer-events is set to let through events to skyview.
//
// Also, give each child a new stacking context (z-index auto). This makes the tooltip, when
// hovering a toolbar icon, to be rendered above the asset menu.
const Component = styled.div<{ assetMenuVisible?: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: grid;
  pointer-events: none;
  grid-template-rows: minmax(0, 1fr) auto;
  grid-template-columns:
    ${(props) => `${props.assetMenuVisible === false ? '' : 'auto '}1fr auto;`}
      ${CollapsableContainerStyled},
    ${RightMenuStyled},
    ${ChipStyled},
    ${CameraViewsStyled},
    ${AsbuiltPanelStyled},
    ${ShareViewStyled} {
    pointer-events: all;
    isolation: isolate;
  }
`;

const ViewerContents = styled(Stack)`
  position: relative;
  flex: 1;
  min-height: 0;
  z-index: ${viewerZIndices.menu};
`;

const Center = styled(Stack)`
  position: relative;
  height: 100%;
  z-index: ${viewerZIndices.menu};
  flex: 1;
`;

const SkyviewMemo = React.memo(Skyview);
export { SkyviewMemo as Skyview };
